import React, { useState } from 'react';
import Select from 'react-select';
import { useDispatch } from 'react-redux'
import { Formik } from "formik";
import Button from "@mui/material/Button";
import * as yup from "yup";
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AppBar from '@mui/material/AppBar';
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions'
import { createExpense } from '../../../redux/actions/apiActions'
import { CircularProgress } from '@mui/material';

export const EXPENSE_TYPES = [
    { value: 'fuel', label: 'Fuel' },
    { value: 'service', label: 'Service' },
    { value: 'repair', label: 'Repair' },
    { value: 'rent', label: 'Rent' },
    { value: 'salary', label: 'Salary' },
    { value: 'office_expenses', label: 'Office Expenses' },
    { value: 'miscellaneous', label: 'Miscellaneous' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'petrol', label: 'Petrol' },
    { value: 'electric', label: 'Electric' },
    { value: 'cng', label: 'CNG' },
];

const validationSchema = yup.object({
    amount: yup
        .number()
        .required("Amount is required.")
        .min(0, "Amount must be positive."),
    type: yup
        .string()
        .required("Expense type is required."),
    description: yup
        .string()
        .trim(),
    createdAt: yup
        .date()
        .required("Date is required."),
})

const AddExpense = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    const [openSidebar, setOpenSidebar] = useState(false);

    const handleSidebarOpen = () => {
        setOpenSidebar(true);
    };

    const handleSidebarClose = () => {
        setOpenSidebar(false);
    };
    const open = isMd ? false : openSidebar;

    const handleOnSubmit = async (values) => {
        dispatch(setGlobalLoader(true))
        try {
            await createExpense(values);
            toast.success("Expense added successfully");
            navigate("/expenses")
            dispatch(setGlobalLoader(false))
        } catch (error) {
            dispatch(setGlobalLoader(false))
            console.log("create expense error", error);
            toast.error(error?.response?.data?.msg ?? "Error creating expense");
        }
    }

    return (
        <Box>
            <AppBar
                position={'fixed'}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar
                onClose={handleSidebarClose}
                open={open}
                variant={isMd ? 'permanent' : 'temporary'}
            />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <br />
                <br />

                <Box
                    overflow="hidden"
                    marginLeft={{ md: '270px' }}
                >
                    <Typography
                        variant={"subtitle2"}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                    >
                        Add Expense
                    </Typography>

                    <Formik
                        initialValues={{
                            amount: '',
                            type: '',
                            description: '',
                            createdAt: new Date().toISOString().split('T')[0],
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values) => handleOnSubmit(values)}
                        enableReinitialize
                    >
                        {(formikProps) => {
                            const { values, errors, touched, setFieldValue, handleSubmit, isSubmitting } =
                                formikProps;
                            return (
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                                        <Grid item xs={12} sm={6} md={7}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 2 }}
                                                fontWeight={700}
                                            >
                                                Expense Type *
                                            </Typography>
                                            <Select
                                                styles={{
                                                    menu: provided => ({ ...provided, zIndex: 9999 })
                                                }}
                                                placeholder="Select Expense Type"
                                                value={EXPENSE_TYPES.find(opt => opt.value === values.type) || null}
                                                options={EXPENSE_TYPES}
                                                onChange={(e) => {
                                                    setFieldValue("type", e.value)
                                                }}
                                            />
                                            {touched.type && errors.type && (
                                                <Typography variant="caption" color="error">
                                                    {errors.type}
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={7}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 2 }}
                                                fontWeight={700}
                                            >
                                                Amount *
                                            </Typography>
                                            <TextField
                                                label="Amount"
                                                variant="outlined"
                                                name={"amount"}
                                                fullWidth
                                                type="number"
                                                InputProps={{
                                                    inputProps: { min: 0 },
                                                }}
                                                value={values.amount}
                                                onChange={(e) =>
                                                    setFieldValue("amount", e.target.value)
                                                }
                                                error={
                                                    touched.amount && Boolean(errors.amount)
                                                }
                                                helperText={touched.amount && errors.amount}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={7}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 2 }}
                                                fontWeight={700}
                                            >
                                                Description
                                            </Typography>
                                            <TextField
                                                label="Description"
                                                variant="outlined"
                                                name={"description"}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                value={values.description}
                                                onChange={(e) =>
                                                    setFieldValue("description", e.target.value)
                                                }
                                                error={
                                                    touched.description && Boolean(errors.description)
                                                }
                                                helperText={touched.description && errors.description}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={7}>
                                            <Typography
                                                variant={"subtitle2"}
                                                sx={{ marginBottom: 2 }}
                                                fontWeight={700}
                                            >
                                                Date *
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                name={"createdAt"}
                                                fullWidth
                                                type="date"
                                                value={values.createdAt}
                                                onChange={(e) =>
                                                    setFieldValue("createdAt", e.target.value)
                                                }
                                                error={
                                                    touched.createdAt && Boolean(errors.createdAt)
                                                }
                                                helperText={touched.createdAt && errors.createdAt}
                                            />
                                        </Grid>
                                    </Grid>
                                    <br />
                                    <Button
                                        component={'button'}
                                        variant={"contained"}
                                        fontWeight={700}
                                        type='submit'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
                                    </Button>
                                </form>
                            )
                        }}
                    </Formik>
                </Box>
            </main>
        </Box>
    );
};

export default AddExpense;
